/**
 * In-stay services panel (Phase 3) — a guest with an active room stay can order food, call staff,
 * request housekeeping/amenities, report a maintenance issue, chat with staff, and view the menu.
 * Opened from "My stays" for a confirmed/checked-in booking; also reused by the room-QR flow (P4).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, UtensilsCrossed, Bell, Sparkles, Wrench, MessageSquare, BookOpen, ChevronLeft, Send, ClipboardList, Users, Copy, Plus, Minus, ShoppingCart } from 'lucide-react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import MenuImagesModal from '@/components/MenuImages';

interface Props {
  bookingId: string;
  branchId?: string;
  roomLabel?: string;   // e.g. "Room 101" or the room type
  accessLevel?: 'full' | 'guest'; // 'guest' = a friend via the booker's QR (call staff + chat only)
  canShare?: boolean;   // booker + checked in → can generate the group QR
  unreadChat?: number;  // unread staff messages to badge the Chat tile on open
  onClose: () => void;
}

type RequestType = 'food_order' | 'call_staff' | 'housekeeping' | 'amenity' | 'maintenance';
interface Req { id: string; requestType: RequestType; category: string | null; note: string | null; status: string; createdAt: string | null; }
interface Msg { id: string; senderType: 'guest' | 'staff'; senderName: string | null; messageText: string; createdAt: string | null; }

const CATEGORIES: Partial<Record<RequestType, string[]>> = {
  housekeeping: ['Room cleaning', 'Extra towels', 'Toiletries', 'Laundry', 'Wake-up call', 'Extra bed'],
  amenity: ['Extra pillow / blanket', 'Drinking water', 'Iron', 'Hair dryer', 'Phone charger'],
  maintenance: ['AC not working', 'Bathroom / plumbing', 'TV / remote', 'Lights / electrical', 'WiFi', 'Other'],
};
const TYPE_TITLE: Record<RequestType, string> = {
  food_order: 'Order food & drink', call_staff: 'Call staff', housekeeping: 'Housekeeping', amenity: 'Amenities', maintenance: 'Report an issue',
};
const STATUS_LABEL: Record<string, string> = { new: 'Received', in_progress: 'In progress', completed: 'Done', cancelled: 'Cancelled' };

const RoomServicePanel: React.FC<Props> = ({ bookingId, branchId, roomLabel, accessLevel = 'full', canShare = false, unreadChat = 0, onClose }) => {
  const isGuest = accessLevel === 'guest';
  const [view, setView] = useState<'home' | 'request' | 'chat' | 'requests' | 'food'>('home');
  const [chatUnread, setChatUnread] = useState(unreadChat);
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  const [reqType, setReqType] = useState<RequestType>('call_staff');
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [callReasons, setCallReasons] = useState<Array<{ label: string; emoji: string | null }>>([]);
  const [shareQr, setShareQr] = useState<{ qrPng: string; url: string } | null>(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    apiService.getRoomCallReasons(branchId).then((r: any) => setCallReasons(Array.isArray(r?.reasons) ? r.reasons : [])).catch(() => {});
  }, [branchId]);

  const openInvite = async () => {
    setShareBusy(true); setError(null);
    try { const r: any = await apiService.generateRoomShareToken(bookingId); setShareQr({ qrPng: r.qrPng, url: r.url }); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not create the invite'); }
    finally { setShareBusy(false); }
  };

  const [requests, setRequests] = useState<Req[]>([]);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  // Structured room food ordering
  interface FoodItem { id: string; itemName: string; itemPrice: number; itemImage: string | null; }
  const [foodCats, setFoodCats] = useState<Array<{ name: string; items: FoodItem[] }>>([]);
  const [foodLoading, setFoodLoading] = useState(false);
  const [cart, setCart] = useState<Record<string, { item: FoodItem; qty: number }>>({});
  const cartCount = Object.values(cart).reduce((s, e) => s + e.qty, 0);
  const cartTotal = Object.values(cart).reduce((s, e) => s + e.qty * e.item.itemPrice, 0);
  const setQty = (item: FoodItem, delta: number) => setCart((c) => {
    const cur = c[item.id]?.qty || 0;
    const next = Math.max(0, cur + delta);
    const n = { ...c };
    if (next === 0) delete n[item.id]; else n[item.id] = { item, qty: next };
    return n;
  });

  const openFood = async () => {
    setView('food'); setError(null); setFoodLoading(true);
    try {
      const res: any = await apiService.getBranchMenu(branchId || '', 'room');
      const cats = Array.isArray(res?.data?.categories) ? res.data.categories : [];
      const flat = cats.map((c: any) => {
        const items: FoodItem[] = [];
        (c.subCategories || []).forEach((s: any) => (s.items || []).forEach((i: any) => items.push({ id: i.id, itemName: i.itemName, itemPrice: Number(i.itemPrice) || 0, itemImage: i.itemImage || null })));
        (c.itemsWithoutSubCategory || []).forEach((i: any) => items.push({ id: i.id, itemName: i.itemName, itemPrice: Number(i.itemPrice) || 0, itemImage: i.itemImage || null }));
        return { name: c.category?.categoryName || 'Menu', items };
      }).filter((g: any) => g.items.length > 0);
      setFoodCats(flat);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not load the menu'); }
    finally { setFoodLoading(false); }
  };

  const placeFoodOrder = async () => {
    const items = Object.values(cart).map((e) => ({ stockItemId: e.item.id, itemName: e.item.itemName, itemImage: e.item.itemImage, unitPrice: e.item.itemPrice, quantity: e.qty }));
    if (items.length === 0) return;
    setSubmitting(true); setError(null);
    try {
      await apiService.createRoomRequest(bookingId, { requestType: 'food_order', items });
      setCart({}); setToast('Food order sent!'); setView('home');
      setTimeout(() => setToast(null), 2500);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not place the order'); }
    finally { setSubmitting(false); }
  };

  const openRequest = (t: RequestType) => { setReqType(t); setCategory(''); setNote(''); setError(null); setView('request'); };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await apiService.createRoomRequest(bookingId, { requestType: reqType, category: category || undefined, note: note || undefined });
      setToast(reqType === 'call_staff' ? 'Staff have been notified.' : 'Request sent!');
      setView('home');
      setTimeout(() => setToast(null), 2500);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not send request'); }
    finally { setSubmitting(false); }
  };

  const loadRequests = useCallback(async () => {
    try { const r: any = await apiService.listRoomRequests(bookingId); setRequests(Array.isArray(r?.requests) ? r.requests : []); } catch { /* noop */ }
  }, [bookingId]);
  const loadChat = useCallback(async () => {
    try { const r: any = await apiService.listRoomChat(bookingId); setMsgs(Array.isArray(r?.messages) ? r.messages : []); } catch { /* noop */ }
  }, [bookingId]);

  useEffect(() => { if (view === 'requests') void loadRequests(); }, [view, loadRequests]);
  useEffect(() => {
    if (view !== 'chat') return;
    setChatUnread(0);
    void loadChat();
    apiService.markRoomChatRead(bookingId).catch(() => {});
    const t = setInterval(() => { void loadChat(); }, 12000); // fallback while chat is open
    return () => clearInterval(t);
  }, [view, loadChat, bookingId]);
  // Live updates (Ably): staff chat replies append; request status changes refresh the list.
  useEffect(() => {
    const { accessToken } = apiService.getTokens();
    const userId = accessToken ? getUserIdFromToken(accessToken) : null;
    const appendMsg = (data: any) => {
      if (data?.roomBookingId !== bookingId) return;
      setMsgs((prev) => (prev.some((m) => m.id === data?.id) ? prev : [...prev, data]));
      if (data?.senderType === 'staff' && viewRef.current !== 'chat') setChatUnread((n) => n + 1);
    };
    const unsubs: Array<() => void> = [];
    unsubs.push(subscribeToChannel(`room_chat:${bookingId}`, ['room_chat:new'], (_e, data) => appendMsg(data)));
    // Staff replies fan out to the booker's user channel — use it too (works even if room_chat:* is restricted).
    if (userId) unsubs.push(subscribeToChannel(`user:${userId}`, ['room_service:updated', 'room_chat:new'], (event, data) => {
      if (event === 'room_service:updated') void loadRequests();
      else appendMsg(data);
    }));
    return () => unsubs.forEach((u) => { try { u(); } catch { /* noop */ } });
  }, [bookingId, loadRequests]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length]);

  const sendChat = async () => {
    const t = text.trim(); if (!t) return;
    try { const r: any = await apiService.sendRoomChat(bookingId, t); if (r?.message) setMsgs((p) => [...p, r.message]); setText(''); } catch { /* noop */ }
  };
  const cancelReq = async (id: string) => {
    try { await apiService.cancelRoomRequest(id); await loadRequests(); } catch { /* noop */ }
  };

  const allTiles: { icon: React.ReactNode; label: string; color: string; onClick: () => void; guest: boolean; show?: boolean; badge?: number }[] = [
    { icon: <Bell size={22} />, label: 'Call staff', color: '#16a34a', onClick: () => openRequest('call_staff'), guest: true },
    { icon: <MessageSquare size={22} />, label: 'Chat with staff', color: '#2563eb', onClick: () => setView('chat'), guest: true, badge: chatUnread },
    { icon: <UtensilsCrossed size={22} />, label: 'Order food', color: '#f59e0b', onClick: () => void openFood(), guest: false },
    { icon: <Sparkles size={22} />, label: 'Housekeeping', color: '#7c3aed', onClick: () => openRequest('housekeeping'), guest: false },
    { icon: <ClipboardList size={22} />, label: 'Amenities', color: '#0ea5e9', onClick: () => openRequest('amenity'), guest: false },
    { icon: <Wrench size={22} />, label: 'Report issue', color: '#dc2626', onClick: () => openRequest('maintenance'), guest: false },
    { icon: <BookOpen size={22} />, label: 'View menu', color: '#0f766e', onClick: () => setShowMenu(true), guest: false },
    { icon: <ClipboardList size={22} />, label: 'My requests', color: '#475569', onClick: () => setView('requests'), guest: true },
  ];
  const tiles = allTiles.filter((t) => t.show !== false && (isGuest ? t.guest : true));

  return (
    <div style={s.overlay}>
      <div style={s.panel}>
        <div style={s.head}>
          {view === 'home'
            ? <button style={s.icoBtn} onClick={onClose} aria-label="Close"><X size={20} /></button>
            : <button style={s.icoBtn} onClick={() => setView('home')} aria-label="Back"><ChevronLeft size={20} /></button>}
          <div style={s.headTitle}>{view === 'home' ? 'Room services' : view === 'chat' ? 'Chat with staff' : view === 'requests' ? 'My requests' : view === 'food' ? 'Order food & drink' : TYPE_TITLE[reqType]}</div>
          <div style={{ width: 32 }} />
        </div>
        {roomLabel && view === 'home' && <div style={s.roomLabel}>{roomLabel}{isGuest ? ' · guest access (call staff & chat)' : ''}</div>}
        {toast && <div style={s.toast}>{toast}</div>}
        {error && <div style={s.error}>{error}</div>}

        <div style={s.body}>
          {view === 'home' && !isGuest && (
            <button style={s.inviteBanner} onClick={() => void openInvite()} disabled={shareBusy}>
              <span style={s.inviteBannerIcon}><Users size={20} /></span>
              <span style={s.inviteBannerText}>
                <b style={s.inviteBannerTitle}>Invite friends to this room</b>
                <span style={s.inviteBannerSub}>Share a QR so they can call staff &amp; chat</span>
              </span>
              <span style={s.inviteBannerCta}>{shareBusy ? '…' : 'Show QR'}</span>
            </button>
          )}
          {view === 'home' && (
            <div style={s.tiles}>
              {tiles.map((tile) => (
                <button key={tile.label} style={{ ...s.tile, position: 'relative' }} onClick={tile.onClick}>
                  <span style={{ ...s.tileIcon, background: tile.color }}>{tile.icon}</span>
                  <span style={s.tileLabel}>{tile.label}</span>
                  {(tile.badge ?? 0) > 0 && <span style={s.tileBadge}>{(tile.badge ?? 0) > 9 ? '9+' : tile.badge}</span>}
                </button>
              ))}
            </div>
          )}

          {view === 'request' && (
            <div>
              {reqType === 'call_staff' && <p style={s.note}>A staff member will come to your room. Add a note if you like.</p>}
              {reqType === 'food_order' && <p style={s.note}>Browse the menu, then type your order below.</p>}
              {(() => {
                const opts = reqType === 'call_staff'
                  ? callReasons.map((r) => ({ key: r.label, label: `${r.emoji ? r.emoji + ' ' : ''}${r.label}`, value: r.label }))
                  : (CATEGORIES[reqType] || []).map((c) => ({ key: c, label: c, value: c }));
                if (opts.length === 0) return null;
                return (
                  <div style={s.chips}>
                    {opts.map((c) => (
                      <button key={c.key} style={{ ...s.chip, ...(category === c.value ? s.chipActive : {}) }} onClick={() => setCategory(c.value)}>{c.label}</button>
                    ))}
                  </div>
                );
              })()}
              <textarea style={s.textarea} rows={3} placeholder={reqType === 'food_order' ? 'e.g. 1x Margherita pizza, 2x Coke' : 'Add details (optional)'} value={note} onChange={(e) => setNote(e.target.value)} />
              <button style={s.primary} onClick={() => void submit()} disabled={submitting}>{submitting ? 'Sending…' : 'Send request'}</button>
            </div>
          )}

          {view === 'requests' && (
            requests.length === 0 ? <p style={s.note}>No requests yet.</p> : (
              <div style={s.list}>
                {requests.map((r) => (
                  <div key={r.id} style={s.reqCard}>
                    <div style={s.reqTop}>
                      <b>{TYPE_TITLE[r.requestType] || r.requestType}{r.category ? ` · ${r.category}` : ''}</b>
                      <span style={{ ...s.badge, ...statusStyle(r.status) }}>{STATUS_LABEL[r.status] || r.status}</span>
                    </div>
                    {r.note && <div style={s.reqNote}>“{r.note}”</div>}
                    {['new', 'in_progress'].includes(r.status) && <button style={s.cancelLink} onClick={() => void cancelReq(r.id)}>Cancel</button>}
                  </div>
                ))}
              </div>
            )
          )}

          {view === 'food' && (
            <div style={s.foodWrap}>
              <button style={s.menuPhotosLink} onClick={() => setShowMenu(true)}><BookOpen size={15} /> View menu photos</button>
              {foodLoading ? (
                <p style={s.note}>Loading menu…</p>
              ) : foodCats.length === 0 ? (
                <p style={s.note}>No items available right now. Tap “View menu photos” or call staff to order.</p>
              ) : (
                <div style={s.foodList}>
                  {foodCats.map((cat) => (
                    <div key={cat.name}>
                      <div style={s.foodCatName}>{cat.name}</div>
                      {cat.items.map((it) => {
                        const qty = cart[it.id]?.qty || 0;
                        return (
                          <div key={it.id} style={s.foodItem}>
                            {it.itemImage && <img src={it.itemImage} alt={it.itemName} style={s.foodImg} />}
                            <div style={s.foodInfo}>
                              <div style={s.foodName}>{it.itemName}</div>
                              <div style={s.foodPrice}>₹{it.itemPrice.toLocaleString('en-IN')}</div>
                            </div>
                            {qty === 0 ? (
                              <button style={s.addBtn} onClick={() => setQty(it, 1)}><Plus size={16} /></button>
                            ) : (
                              <div style={s.stepper}>
                                <button style={s.stepBtn} onClick={() => setQty(it, -1)}><Minus size={14} /></button>
                                <span style={s.stepQty}>{qty}</span>
                                <button style={s.stepBtn} onClick={() => setQty(it, 1)}><Plus size={14} /></button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
              {cartCount > 0 && (
                <button style={s.placeOrderBtn} onClick={() => void placeFoodOrder()} disabled={submitting}>
                  <ShoppingCart size={17} /> {submitting ? 'Sending…' : `Place order · ${cartCount} item${cartCount > 1 ? 's' : ''} · ₹${cartTotal.toLocaleString('en-IN')}`}
                </button>
              )}
            </div>
          )}

          {view === 'chat' && (
            <div style={s.chatWrap}>
              <div style={s.chatBody}>
                {msgs.length === 0 ? <p style={s.note}>Say hello 👋 — staff will reply here.</p> :
                  msgs.map((m) => (
                    <div key={m.id} style={{ ...s.msg, ...(m.senderType === 'guest' ? s.msgMine : s.msgStaff) }}>
                      {m.senderType === 'staff' && <div style={s.msgName}>{m.senderName || 'Reception'}</div>}
                      {m.messageText}
                    </div>
                  ))}
                <div ref={endRef} />
              </div>
              <div style={s.chatInput}>
                <input style={s.input} value={text} onChange={(e) => setText(e.target.value)} placeholder="Message staff…" onKeyDown={(e) => { if (e.key === 'Enter') void sendChat(); }} />
                <button style={s.sendBtn} onClick={() => void sendChat()} disabled={!text.trim()}><Send size={16} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showMenu && branchId && <MenuImagesModal branchId={branchId} branchName={roomLabel} onClose={() => { setShowMenu(false); if (view === 'home') openRequest('food_order'); }} />}

      {shareBusy && <div style={s.busyToast}>Creating invite…</div>}
      {shareQr && (
        <div style={s.overlay} onClick={() => setShareQr(null)}>
          <div style={{ ...s.panel, height: 'auto', maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={s.head}>
              <div style={{ width: 32 }} />
              <div style={s.headTitle}>Invite friends</div>
              <button style={s.icoBtn} onClick={() => setShareQr(null)} aria-label="Close"><X size={20} /></button>
            </div>
            <div style={{ padding: '18px 20px', textAlign: 'center' }}>
              <p style={s.note}>Friends in your room scan this to <b>call staff</b> and <b>chat with reception</b>. It stops working when you check out.</p>
              <img src={shareQr.qrPng} alt="Room invite QR" style={{ width: 220, height: 220 }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <input readOnly value={shareQr.url} style={{ ...s.input, fontSize: '0.72rem' }} />
                <button style={s.sendBtn} onClick={() => { void navigator.clipboard?.writeText(shareQr.url); setToast('Link copied'); setTimeout(() => setToast(null), 1500); }}><Copy size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function statusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'in_progress': return { background: '#dbeafe', color: '#1d4ed8' };
    case 'completed': return { background: '#dcfce7', color: '#15803d' };
    case 'cancelled': return { background: '#fee2e2', color: '#b91c1c' };
    default: return { background: '#fef3c7', color: '#b45309' };
  }
}

const s: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1300 },
  panel: { background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: 'min(520px,100vw)', height: 'min(760px,94vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 30px rgba(0,0,0,0.25)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px', borderBottom: '1px solid #e5e7eb' },
  icoBtn: { background: '#f1f5f9', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155' },
  headTitle: { fontWeight: 800, fontSize: '1rem', color: '#0f172a' },
  roomLabel: { textAlign: 'center', fontSize: '0.82rem', color: '#64748b', padding: '8px 0 0' },
  toast: { margin: '8px 14px 0', background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: '0.84rem', textAlign: 'center' },
  busyToast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'rgba(15,23,42,0.9)', color: '#fff', borderRadius: 9999, padding: '8px 16px', fontSize: '0.82rem', zIndex: 1500 },
  error: { margin: '8px 14px 0', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem' },
  body: { flex: 1, overflowY: 'auto', padding: '14px' },
  inviteBanner: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: 'linear-gradient(135deg, #0891b2, #0e7490)', color: '#fff', border: 'none', borderRadius: 14, padding: '13px 15px', marginBottom: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(8,145,178,0.3)' },
  inviteBannerIcon: { flexShrink: 0, width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  inviteBannerText: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 },
  inviteBannerTitle: { fontSize: '0.92rem', fontWeight: 800 },
  inviteBannerSub: { fontSize: '0.76rem', opacity: 0.9 },
  inviteBannerCta: { flexShrink: 0, background: '#fff', color: '#0e7490', fontWeight: 800, fontSize: '0.8rem', borderRadius: 9999, padding: '7px 13px' },
  tiles: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  tile: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '18px 8px', cursor: 'pointer' },
  tileIcon: { width: 46, height: 46, borderRadius: 12, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  tileLabel: { fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' },
  tileBadge: { position: 'absolute', top: 8, right: 10, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 9999, background: '#ef4444', color: '#fff', fontSize: '0.7rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #fff' },
  note: { color: '#64748b', fontSize: '0.88rem', textAlign: 'center', padding: '10px 6px' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  chip: { border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 100, padding: '7px 12px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  chipActive: { background: '#ff6b35', color: '#fff', borderColor: '#ff6b35' },
  textarea: { width: '100%', border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px', fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box' },
  primary: { width: '100%', marginTop: 12, background: '#ff6b35', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  reqCard: { border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 12px' },
  reqTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: '#0f172a' },
  reqNote: { fontSize: '0.82rem', color: '#475569', marginTop: 4 },
  badge: { borderRadius: 100, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700 },
  cancelLink: { marginTop: 8, background: 'none', border: 'none', color: '#dc2626', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0 },
  foodWrap: { display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 },
  menuPhotosLink: { alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#334155', borderRadius: 9999, padding: '7px 13px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' },
  foodList: { display: 'flex', flexDirection: 'column', gap: 14 },
  foodCatName: { fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', margin: '2px 0 8px', textTransform: 'uppercase', letterSpacing: '0.03em' },
  foodItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' },
  foodImg: { width: 44, height: 44, borderRadius: 9, objectFit: 'cover', flexShrink: 0 },
  foodInfo: { flex: 1, minWidth: 0 },
  foodName: { fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' },
  foodPrice: { fontSize: '0.82rem', color: '#64748b', marginTop: 1 },
  addBtn: { flexShrink: 0, width: 34, height: 34, borderRadius: 9, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 700 },
  stepper: { flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', borderRadius: 9, padding: 3 },
  stepBtn: { width: 28, height: 28, borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  stepQty: { minWidth: 20, textAlign: 'center', fontWeight: 800, color: '#1e3a8a', fontSize: '0.9rem' },
  placeOrderBtn: { position: 'sticky', bottom: 0, marginTop: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 11, padding: '13px', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', boxShadow: '0 4px 14px rgba(22,163,74,0.35)' },
  chatWrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  chatBody: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', padding: 10, minHeight: 200, background: '#f9fafb', borderRadius: 10 },
  msg: { maxWidth: '80%', padding: '7px 10px', borderRadius: 12, fontSize: '0.88rem', lineHeight: 1.35 },
  msgMine: { alignSelf: 'flex-end', background: '#2563eb', color: '#fff', borderBottomRightRadius: 4 },
  msgStaff: { alignSelf: 'flex-start', background: '#fff', color: '#111827', border: '1px solid #e5e7eb', borderBottomLeftRadius: 4 },
  msgName: { fontSize: '0.68rem', fontWeight: 600, color: '#b45309', marginBottom: 2 },
  chatInput: { display: 'flex', gap: 8, paddingTop: 8, borderTop: '1px solid #eef1f5' },
  input: { flex: 1, border: '1px solid #cbd5e1', borderRadius: 9, padding: '10px', fontSize: '0.9rem' },
  sendBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 9, padding: '0 16px', cursor: 'pointer' },
};

export default RoomServicePanel;
